import clsx from "clsx";
import { useId } from "react";

import { Column, LoadingIndicator, Row } from "@/components";
import { Card } from "@/components/ui/card";
import {
  HEALTH_CATEGORIES,
  HealthCategory,
} from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { useGetPortfolioJobHealth } from "@/hooks/queries/portfolio/useGetPortfolioJobHealth";
import {
  getHealthTier,
  healthTierToHexColor,
  healthTierToLabel,
} from "@/lib/constants";
import { jobHealthGradient } from "@/lib/jobHealthGradient";
import { cn } from "@/lib/utils";
import { Job, JobStatus, PortfolioHealthScoreTier } from "@/types";

const CATEGORY_LABELS: Record<HealthCategory, string> = {
  volume: "Volume",
  velocity: "Velocity",
  quality: "Quality",
  engagement: "Engagement",
};

const CATEGORY_DESCRIPTIONS: Record<HealthCategory, string> = {
  volume: "Candidate flow",
  velocity: "Pipeline movement",
  quality: "Candidate fit",
  engagement: "Recent activity",
};

const CATEGORY_WEIGHTS: Record<HealthCategory, string> = {
  volume: "30%",
  velocity: "30%",
  quality: "20%",
  engagement: "20%",
};

const TIER_DESCRIPTIONS: Record<PortfolioHealthScoreTier, string> = {
  healthy: "Pipeline is moving well with strong supporting signals.",
  watch: "Mostly on track with a few signals worth watching.",
  "at-risk": "Multiple signals need attention before this job slips.",
  critical:
    "Active blocker or multiple components broken. Escalation territory.",
};

const PENDING_SCORE_COLOR = "#94A3B8";

const formatScore = (score: number | null) =>
  score == null ? "--" : Math.round(score).toLocaleString();

const getHealthStatus = (score: number | null) => {
  if (score == null) {
    return {
      color: PENDING_SCORE_COLOR,
      description: "Not enough data yet to calculate job health.",
      label: "Pending",
    };
  }
  const tier = getHealthTier(score);
  return {
    color: healthTierToHexColor[tier],
    description: TIER_DESCRIPTIONS[tier],
    label: healthTierToLabel[tier],
  };
};

const Gauge = ({ score }: { score: number | null }) => {
  const gradientId = `job-health-gradient-${useId().replace(/:/g, "")}`;
  const percent = Math.min(Math.max(score ?? 0, 0), 100);

  return (
    <div className="relative h-44 w-full max-w-[280px]">
      <svg className="h-full w-full" viewBox="0 0 200 140">
        <defs>
          <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
            {jobHealthGradient.stops.map((stop) => (
              <stop
                key={stop.offset}
                offset={`${stop.offset.toString()}%`}
                stopColor={stop.color}
              />
            ))}
          </linearGradient>
        </defs>
        <path
          d="M 25 118 A 75 75 0 0 1 175 118"
          fill="none"
          pathLength={100}
          stroke="#E5E7EB"
          strokeWidth="24"
        />
        <path
          d="M 25 118 A 75 75 0 0 1 175 118"
          fill="none"
          pathLength={100}
          stroke={`url(#${gradientId})`}
          strokeDasharray="100"
          strokeDashoffset={(100 - percent).toString()}
          strokeLinecap="butt"
          strokeWidth="24"
        />
      </svg>
      <Column className="absolute inset-x-0 top-[106px] items-center">
        <p className="text-5xl font-semibold leading-none text-slate-950">
          {formatScore(score)}
        </p>
      </Column>
    </div>
  );
};

const ScoreBreakdownRow = ({
  label,
  reason,
  score,
  subtitle,
  weight,
}: {
  label: string;
  reason?: string;
  score: number | null;
  subtitle: string;
  weight: string;
}) => {
  return (
    <Column className="gap-2 rounded-lg border border-slate-200 bg-white p-3">
      <Row className="items-start justify-between gap-3">
        <Column className="min-w-0 gap-0.5">
          <Row className="items-center gap-2">
            <p className="text-sm font-semibold text-slate-900">{label}</p>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
              {weight}
            </span>
          </Row>
          <p className="text-xs font-medium text-slate-500">{subtitle}</p>
        </Column>
        <p className="shrink-0 text-sm font-semibold tabular-nums text-slate-900">
          {formatScore(score)}
        </p>
      </Row>
      <p className="text-xs leading-relaxed text-slate-600">
        {reason ?? "No reasoning available yet."}
      </p>
    </Column>
  );
};

export const JobStats = ({
  className,
  job,
}: {
  className?: string;
  job: Job;
}) => {
  const healthApi = useGetPortfolioJobHealth({
    enabled: job.status !== JobStatus.DRAFT,
    jobId: job.id,
  });
  const healthData = healthApi.data;
  const healthScores = healthData?.health;
  const healthStatus = getHealthStatus(healthData?.score ?? null);

  const renderHeader = () => {
    if (job.status === JobStatus.DRAFT) {
      return (
        <p className="text-sm font-medium text-slate-500">
          Post job to start seeing health signals.
        </p>
      );
    }
    if (healthApi.isLoading) {
      return <LoadingIndicator />;
    }
    if (healthApi.isError) {
      return (
        <p className="text-sm font-medium text-slate-500">
          Job health unavailable.
        </p>
      );
    }
    if (!healthData)
      return (
        <p className="text-sm font-medium text-slate-500">
          Not enough health signal yet.
        </p>
      );
    return (
      <>
        <Gauge score={healthData.score} />
        <Column className="w-full items-center gap-2 text-center">
          <Row className="items-baseline justify-center gap-3">
            <p
              className={cn("text-xl font-bold uppercase")}
              style={{ color: healthStatus.color }}
            >
              {healthStatus.label}
            </p>
            <p className="text-sm font-semibold text-slate-400">
              Health score {formatScore(healthData.score)}/100
            </p>
          </Row>
          <p className="max-w-[284px] text-[15px] font-medium leading-snug text-slate-500">
            {healthStatus.description}
          </p>
        </Column>
      </>
    );
  };
  return (
    <Column className={clsx("gap-4 bg-slate-50", className)}>
      <Row className="items-center justify-between gap-3">
        <p className="font-semibold text-slate-900">Job Overview</p>
        {healthData?.computedAt ? (
          <p className="text-xs font-medium text-slate-500">
            Updated {new Date(healthData.computedAt).toLocaleDateString()}
          </p>
        ) : null}
      </Row>

      <Card className="flex flex-col items-center gap-4 rounded-xl border-slate-200 bg-white p-5 shadow-sm">
        {renderHeader()}
      </Card>

      {healthScores ? (
        <Column className="gap-3">
          <p className="text-base font-semibold text-slate-900">
            Score breakdown
          </p>
          {HEALTH_CATEGORIES.map((category) => (
            <ScoreBreakdownRow
              key={category}
              label={CATEGORY_LABELS[category]}
              reason={healthData.healthReasons?.[category]}
              score={healthScores[category]}
              subtitle={CATEGORY_DESCRIPTIONS[category]}
              weight={CATEGORY_WEIGHTS[category]}
            />
          ))}
        </Column>
      ) : null}
    </Column>
  );
};
